import React, { useEffect, useState } from 'react';
import { Modal, Form, Col, Row } from 'react-bootstrap';
import { HeaderText } from "../../Controls/HeaderText";
import { useAtom } from "jotai";
import { createApiService } from "../../shared/apiConfig";
import { FeesApi, FacilityApi } from "../../shared/api/axios-client";
import { Select } from "../../Controls/Select";
import { Button } from "../../Controls/Button";
import { toast } from "react-toastify";
import { showMoney, cents } from "../../Data/Money";
import { 
    FeeInfo, 
    FeeCalculationResult, 
    formatFeeAmount, 
    getFeeScopeText, 
    getFeeCalcText, 
    getPaymentTypeText,
    calculateFeeTotals 
} from "../../Data/FeeCalculations";
import { debug } from 'console';
import { de } from '@faker-js/faker';

interface TestFacilityModalProps {
    show: boolean;
    setShow: (show: boolean) => void;
    operators: any[];
}

export function TestFacilityModal({ show, setShow, operators }: TestFacilityModalProps) {
    
    const feesService = createApiService(FeesApi);
    const facilityService = createApiService(FacilityApi);
    
    const [operatorFacilities, setOperatorFacilities] = useState<any[]>([]);
    const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
    const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null);
    const [testResults, setTestResults] = useState<FeeCalculationResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchOperatorFacilities = async (operatorId: number) => {
        try {
            const response = await facilityService.listFacilities({
                operatorId: operatorId,
                pageSize: 1000,
                pagination: true
            });
            setOperatorFacilities(response.data?.items || []);
        } catch (error) {
            console.error("Error fetching operator facilities:", error);
            setOperatorFacilities([]);
        }
    };

    const handleOperatorChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const operatorId = e.target.value ? parseInt(e.target.value) : null;
        setSelectedOperatorId(operatorId);
        setSelectedFacilityId(null);
        setTestResults(null);
        
        if (operatorId) {
            await fetchOperatorFacilities(operatorId);
        } else {
            setOperatorFacilities([]);
        }
    };

    const handleFacilityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const facilityId = e.target.value ? parseInt(e.target.value) : null;
        setSelectedFacilityId(facilityId);
        setTestResults(null);
    };

    const runTestCalculation = async () => {
        if (!selectedFacilityId) return;

        try {
            setIsLoading(true);

            const response = await feesService.testCalculation({
                facilityId: selectedFacilityId
            });

            // Transform API response to our expected format
            const apiData = response.data as any;
            console.log('API Response:', apiData); // Debug log
            
            // Handle different possible response structures
            const dataArray = Array.isArray(apiData) ? apiData : apiData?.feeCalculations || apiData?.data || apiData?.items || [];
            console.log('Data Array:', dataArray); // Debug log
            
            if (!Array.isArray(dataArray)) {
                console.error('Expected array but got:', typeof dataArray, dataArray);
                toast.error('Invalid response format from API');
                return;
            }
            
            // Flatten the nested structure and extract all fees
            const fees: FeeInfo[] = [];
            let totalParkerFees = 0;
            let totalOperatorFees = 0;
            
            dataArray.forEach((scopeGroup: any) => {
                const feeScope = scopeGroup.feeScope || 0;

                const paymentType = scopeGroup.feePaymentType || 0;
                
                if (scopeGroup.feeCalculations?.feeCalculations) {
                    scopeGroup.feeCalculations.feeCalculations.forEach((fee: any) => {
                        fees.push({
                            feeName: fee.name || '',
                            amount: fee.amount || 0,
                            feeCalc: fee.feeCalc || 0,
                            feeScope: feeScope,
                            paymentType: paymentType,
                            isAppliedToParker: fee.isPaidByParker || false
                        });
                        
                        if (fee.isPaidByParker) {
                            totalParkerFees += fee.amount || 0;
                        } else {
                            totalOperatorFees += fee.amount || 0;
                        }
                    });
                }
            });

            // Create calculation result with the actual totals from API
            const calculationResult: FeeCalculationResult = {
                baseAmount: 10, // $10 base amount
                fees: fees,
                totalAmount: 10 + (totalParkerFees + totalOperatorFees) / 100, // Convert cents to dollars
                facilityCharge: totalOperatorFees / 100, // Convert cents to dollars
                parkerCharge: totalParkerFees / 100 // Convert cents to dollars
            };

            setTestResults(calculationResult);
        
        } catch (error: any) {
            console.error("Error running test calculation:", error);
            toast.error(error.response?.data?.message || 'Error running test calculation');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setShow(false);
        setSelectedOperatorId(null);
        setSelectedFacilityId(null);
        setTestResults(null);
        setOperatorFacilities([]);
    };



    if (!show) {
        return <></>;
    }

    return (
        <Modal show={show} centered size="lg" onHide={handleClose}>
            <Modal.Header>
                <Modal.Title>Test Facility Fee Calculation</Modal.Title>
                <button
                    type="button"
                    className="btn-close"
                    onClick={handleClose}
                    aria-label="Close"
                ></button>
            </Modal.Header>
            <Modal.Body>
                <Row className="mb-0 align-items-center">
                    <Col md={3}>
                        <Form.Label className="mb-0 fw-bold">Operator *</Form.Label>
                    </Col>
                    <Col md={9}>
                        <Select
                            label=""
                            value={selectedOperatorId || ''}
                            onChange={handleOperatorChange}
                        >
                            <option value="">Select Operator</option>
                            {operators.map(operator => (
                                <option key={operator.operatorId} value={operator.operatorId}>
                                    {operator.name}
                                </option>
                            ))}
                        </Select>
                    </Col>
                </Row>

                <Row style={{ marginTop: '-20px' }} className="mb-2 align-items-center">
                    <Col md={3}>
                        <Form.Label className="mb-0 fw-bold">Facility *</Form.Label>
                    </Col>
                    <Col md={9}>
                        <Select
                            label=""
                            value={selectedFacilityId || ''}
                            onChange={handleFacilityChange}
                            disabled={!selectedOperatorId}
                        >
                            <option value="">Select Facility</option>
                            {operatorFacilities.map(facility => (
                                <option key={facility.facilityId} value={facility.facilityId}>
                                    {facility.name}
                                </option>
                            ))}
                        </Select>
                    </Col>
                </Row>

             
                    <Row >
                        <Col>
                            <Button
                                onClick={runTestCalculation}
                                disabled={isLoading || !selectedFacilityId}
                                className="btn-main"
                            >
                                {isLoading ? 'Running Test...' : 'Run Test Calculation'}
                            </Button>
                        </Col>
                    </Row>
             

                {testResults && (
                    <Row className="my-4">
                        <Col>
                            {testResults.fees.length > 0 ? (
                                <>
                                    <h6 className="text-muted mb-3">
                                        <i className="bi bi-calculator me-2"></i>
                                        Fee Calculation Results (Based on $10 Base Amount)
                                    </h6>
                            
                            {/* Group fees by scope and payment type */}
                            {(() => {
                                const groupedFees = testResults.fees.reduce((groups: any, fee) => {
                                    const key = `${fee.feeScope}-${fee.paymentType}`;
                                    if (!groups[key]) {
                                        groups[key] = {
                                            scope: fee.feeScope,
                                            paymentType: fee.paymentType,
                                            fees: [],
                                            totalFees: 0,
                                            parkerFees: 0,
                                            facilityFees: 0
                                        };
                                    }
                                    groups[key].fees.push(fee);
                                    groups[key].totalFees += fee.amount / 100; // Convert cents to dollars
                                    if (fee.isAppliedToParker) {
                                        groups[key].parkerFees += fee.amount / 100;
                                    } else {
                                        groups[key].facilityFees += fee.amount / 100;
                                    }
                                    return groups;
                                }, {});

                                return Object.values(groupedFees).map((group: any, index) => (
                                    <div key={index} className="card mb-3">
                                        <div className="card-header bg-light">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">
                                                    {getFeeScopeText(group.scope)} - {getPaymentTypeText(group.paymentType)}
                                                </h6>
                                                <span className="badge bg-primary">
                                                    {group.fees.length} fee{group.fees.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <Row className="mb-3">
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">Base Amount:</span>
                                                        <span className="fw-bold">$10.00</span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">Parker Fees:</span>
                                                        <span className="fw-bold text-primary">
                                                            ${group.parkerFees.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted">Parker Total:</span>
                                                        <span className="fw-bold fs-5 text-dark">
                                                            ${(10 + group.parkerFees).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">Parker Pays:</span>
                                                        <span className={`fw-bold ${group.parkerFees > 0 ? 'text-primary' : 'text-muted'}`}>
                                                            ${group.parkerFees.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <span className="text-muted">Facility Pays:</span>
                                                        <span className={`fw-bold ${group.facilityFees > 0 ? 'text-success' : 'text-muted'}`}>
                                                            ${group.facilityFees.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted">Remitted to Facility:</span>
                                                        <span className="fw-bold text-info">
                                                            ${(10 - group.facilityFees).toFixed(2)}
                                                        </span>
                                                    </div>
                                                </Col>
                                            </Row>

                                            {group.fees.length > 0 && (
                                                <div className="table-responsive">
                                                    <table className="table table-sm mb-0">
                                                        <thead>
                                                            <tr>
                                                                <th>Fee Name</th>
                                                                <th>Amount</th>
                                                                <th>Applied To</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {group.fees.map((fee: any, feeIndex: number) => (
                                                                <tr key={feeIndex}>
                                                                    <td>{fee.feeName}</td>
                                                                    <td>{formatFeeAmount(fee.amount, fee.feeCalc)}</td>
                                                                    <td>
                                                                        <span className={`badge ${fee.isAppliedToParker ? 'bg-primary' : 'bg-success'}`}>
                                                                            {fee.isAppliedToParker ? 'Parker' : 'Facility'}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ));
                            })()}
                                </>
                            ) : (
                                <div className="text-center py-4">
                                    <i className="bi bi-info-circle text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                                    <h6 className="text-muted">No fees apply for this facility</h6>
                                    <p className="text-muted small mb-0">The selected facility has no configured fees.</p>
                                </div>
                            )}
                        </Col>
                    </Row>
                )}

                <div className="d-flex justify-content-end gap-2">
                    <Button
                        type="button"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
} 